import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Waves, Volume2, Activity } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyTinnitusTestPreview - ÉCRAN 4 : Aperçu Test Acouphènes
 * Aperçu non interactif du test d'acouphènes (fréquence/intensité)
 */
const JourneyTinnitusTestPreview = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [freqValue, setFreqValue] = useState(4000);
  const [intensityValue, setIntensityValue] = useState(50);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  useEffect(() => {
    setIsFullscreen(true);
    
    // Fade-in progressif
    setTimeout(() => setShowContent(true), 300);
    
    // Animation des sliders (simulation)
    const interval = setInterval(() => {
      setFreqValue(prev => {
        const newVal = prev + 500;
        return newVal > 8000 ? 2000 : newVal;
      });
      setIntensityValue(prev => {
        const newVal = prev + 10;
        return newVal > 80 ? 30 : newVal;
      });
    }, 2000);
    
    return () => {
      clearInterval(interval);
      setIsFullscreen(false);
    };
  }, [setIsFullscreen]);

  const handleContinue = () => {
    console.log('🌀 JourneyTinnitusTestPreview: Navigating to /journey-therapeutic-test');
    navigate('/journey-therapeutic-test');
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal - Fade-in + Translation */}
      <div
        className="relative z-10 max-w-md w-full transition-all duration-700 ease-out"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(30px)',
        }}
      >
        {/* Bandeau pédagogique premium */}
        <div 
          className="mb-8 px-6 py-4 rounded-2xl"
          style={{
            background: 'rgba(168, 85, 247, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(168, 85, 247, 0.3)',
          }}
        >
          <div className="flex items-start gap-3">
            <Waves className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white/90 text-sm leading-relaxed">
                {isEnglish
                  ? 'You identify your tinnitus frequency to personalize your sound relief.'
                  : 'Vous identifiez la fréquence de votre acouphène pour personnaliser votre apaisement sonore.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Aperçu du Test Acouphènes */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 mb-8"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              {isEnglish ? 'Tinnitus Test' : 'Test Acouphènes'}
            </h2>
            <p className="text-purple-400 text-sm flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              {isEnglish ? 'Frequency & Intensity' : 'Fréquence & Intensité'}
            </p>
          </div>

          {/* Visualisation de la fréquence (onde) */}
          <div className="mb-6 flex justify-center">
            <svg width="240" height="80" viewBox="0 0 240 80">
              <path
                d={`M 0 40 ${Array.from({ length: 24 }, (_, i) => {
                  const x = i * 10;
                  const amplitude = 30 * (intensityValue / 100);
                  const frequency = freqValue / 200;
                  const y = 40 + Math.sin((x * frequency) / 10) * amplitude;
                  return `L ${x} ${y}`;
                }).join(' ')}`}
                stroke="rgba(168, 85, 247, 0.8)"
                strokeWidth="2"
                fill="none"
                style={{
                  filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.6))',
                  transition: 'all 2s ease-in-out',
                }}
              />
            </svg>
          </div>

          {/* Sliders animés (aperçu) */}
          <div className="space-y-6 opacity-80">
            {/* Fréquence slider */}
            <div>
              <div className="flex items-center justify-between text-xs text-white/70 mb-2">
                <span>{isEnglish ? 'Frequency' : 'Fréquence'}</span>
                <span className="text-purple-400 font-semibold">{freqValue} Hz</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-2000"
                  style={{
                    width: `${((freqValue - 2000) / 6000) * 100}%`,
                    background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.6), rgba(168, 85, 247, 1))',
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-white/50 mt-1">
                <span>2000 Hz</span>
                <span>8000 Hz</span>
              </div>
            </div>

            {/* Intensité slider */}
            <div>
              <div className="flex items-center justify-between text-xs text-white/70 mb-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  <span>{isEnglish ? 'Intensity' : 'Intensité'}</span>
                </div>
                <span className="text-purple-400 font-semibold">{intensityValue}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-2000"
                  style={{
                    width: `${intensityValue}%`,
                    background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.6), rgba(168, 85, 247, 1))',
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-white/50 mt-1">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Badge Preview */}
          <div className="text-center text-white/50 text-sm mt-6">
            {isEnglish ? 'Preview Mode' : 'Mode Aperçu'}
          </div>
        </div>

        {/* CTA Continuer */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className="px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white"
            style={{
              background: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
              boxShadow: '0 8px 30px rgba(168, 85, 247, 0.4)',
            }}
          >
            {isEnglish ? 'Continue' : 'Continuer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyTinnitusTestPreview;
